import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CargaMasivaRepository } from 'src/app/domain/repository/carga-masiva.repository';
import { CargaMasivaResumen, FilaResultado } from '@model/carga-masiva';

/**
 * Adaptador HTTP: HttpCargaMasivaRepository
 *
 * Implementa el puerto CargaMasivaRepository para comunicación HTTP con backend.
 * Realiza llamadas REST al backend Spring Boot (CargaMasivaController).
 *
 * Arquitectura Hexagonal:
 * - Este adaptador está en infraestructura
 * - Implementa el puerto CargaMasivaRepository del dominio
 *
 * Backend: 580-girop_servicios-ms (puerto 8080)
 * los resuelve el backend desde el contexto SSO; el frontend nunca envía
 * datos sensibles en headers.
 *
 * @author GIROP Migration
 */
@Injectable({ providedIn: 'root' })
export class HttpCargaMasivaRepository extends CargaMasivaRepository {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.backend.baseUrl}/servicios/carga-masiva`;

  /**
   * Descarga el archivo Excel de errores para gestiones masivas.
   * POST /api/servicios/carga-masiva/gestiones-masivas/errores
   * Body: FilaResultado[] (solo filas con error)
   * Response: Blob (Excel)
   */
  descargarErroresGestionesMasivas(
    filasConError: FilaResultado[]
  ): Observable<Blob> {
    return this.http.post(
      `${this.baseUrl}/gestiones-masivas/errores`,
      filasConError,
      {
        headers: { 'Content-Type': 'application/json' },
        responseType: 'blob'
      }
    );
  }

  override descargarErroresCumplimientos(
    filasConError: FilaResultado[]
  ): Observable<Blob> {
    return this.http.post(
      `${this.baseUrl}/cumplimientos-sin-oc/errores`,
      filasConError,
      {
        headers: { 'Content-Type': 'application/json' },
        responseType: 'blob'
      }
    );
  }

  /**
   * Procesa un archivo Excel para crear citas masivamente.
   *
   * POST /api/servicios/carga-masiva/crear-citas
   * Content-Type: multipart/form-data
   */
  override crearCitasMasivamente(
    archivo: File
  ): Observable<CargaMasivaResumen> {
    const formData = new FormData();
    formData.append('archivo', archivo, archivo.name);

    return this.http.post<CargaMasivaResumen>(
      `${this.baseUrl}/crear-citas`,
      formData
    );
  }

  /**
   * Procesa un archivo Excel para cancelar citas masivamente.
   *
   * POST /api/servicios/carga-masiva/cancelar-citas
   * Content-Type: multipart/form-data
   */
  override cancelarCitasMasivamente(
    archivo: File
  ): Observable<CargaMasivaResumen> {
    const formData = new FormData();
    formData.append('archivo', archivo, archivo.name);

    return this.http.post<CargaMasivaResumen>(
      `${this.baseUrl}/cancelar-citas`,
      formData
    );
  }

  /**
   * Procesa un archivo Excel para registrar gestiones masivamente.
   * Legacy: LectorGestiones.java / ClaseLectorExcel.LECTOR_GESTIONES_MASIVO
   *
   * POST /api/servicios/carga-masiva/gestiones-masivas
   * Content-Type: multipart/form-data
   */
  override procesarGestionesMasivas(
    archivo: File
  ): Observable<CargaMasivaResumen> {
    const formData = new FormData();
    formData.append('archivo', archivo, archivo.name);

    return this.http.post<CargaMasivaResumen>(
      `${this.baseUrl}/gestiones-masivas`,
      formData
    );
  }

  /**
   * Procesa un archivo Excel para registrar cumplimientos masivos Sin OC.
   * Legacy: LectorCumplimientosSinOC.java / ClaseLectorExcel.LECTOR_CUMPLIMIENTOS_SINOC
   *
   * POST /api/servicios/carga-masiva/cumplimientos-sin-oc
   * Content-Type: multipart/form-data
   */
  override procesarCumplimientosMasivos(
    archivo: File
  ): Observable<CargaMasivaResumen> {
    const formData = new FormData();
    formData.append('archivo', archivo, archivo.name);

    return this.http.post<CargaMasivaResumen>(
      `${this.baseUrl}/cumplimientos-sin-oc`,
      formData
    );
  }

  /**
   * Obtiene la URL de descarga de la plantilla.
   *
   * GET /api/servicios/carga-masiva/plantillas/{tipo}
   */
  override getPlantillaUrl(
    tipo: Parameters<CargaMasivaRepository['getPlantillaUrl']>[0]
  ): string {
    return `${this.baseUrl}/plantillas/${tipo}`;
  }
}
